import { useCallback, useEffect, useState } from "react";
import { EPICOR_APP_ID, EPICOR_PROVIDER_ID } from "@forgepoint/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConnectionsStore } from "@/store/connectionsStore";
import { toast } from "sonner";

interface EpicorConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
}

export function EpicorConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
}: EpicorConnectionDialogProps) {
  const [baseUrl, setBaseUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [requiresEgressProxy, setRequiresEgressProxy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const fetchConnectionsByApp = useConnectionsStore(
    (state) => state.fetchConnectionsByApp,
  );

  useEffect(() => {
    if (!open) {
      setBaseUrl("");
      setUsername("");
      setPassword("");
      setApiKey("");
      setRequiresEgressProxy(false);
      setError(null);
      setIsSubmitting(false);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    const trimmedBaseUrl = baseUrl.trim().replace(/\/+$/, "");
    const trimmedUsername = username.trim();
    const trimmedApiKey = apiKey.trim();
    try {
      new URL(trimmedBaseUrl);
    } catch {
      setError("Provide a valid Epicor Kinetic base URL.");
      return;
    }
    if (!trimmedUsername || !password) {
      setError("Provide the Epicor username and password.");
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const connection = await finalizeConnection({
        connectionProviderId: EPICOR_PROVIDER_ID,
        appId: EPICOR_APP_ID,
        credentials: {
          type: "EpicorServiceCredentials",
          username: trimmedUsername,
          password,
          ...(trimmedApiKey ? { apiKey: trimmedApiKey } : {}),
        },
        config: { baseUrl: trimmedBaseUrl },
        requiresEgressProxy,
        name: "Epicor Kinetic",
      });
      if (!connection) {
        setError(
          "Epicor connection validation failed. Check the credentials and URL.",
        );
        return;
      }
      toast.success("Epicor connection created successfully.");
      await fetchConnectionsByApp(EPICOR_APP_ID);
      onSuccess?.(connection.id);
      onOpenChange(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to create Epicor connection.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    baseUrl,
    username,
    password,
    apiKey,
    requiresEgressProxy,
    fetchConnectionsByApp,
    finalizeConnection,
    onOpenChange,
    onSuccess,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect Epicor Kinetic</DialogTitle>
          <DialogDescription>
            Enter the Epicor tenant and service-account credentials. Credentials
            are stored with the connection and are not returned after setup.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="epicor-base-url">Base URL</Label>
            <Input
              id="epicor-base-url"
              type="url"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="https://epicor.example.com/Kinetic"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="epicor-username">Username</Label>
            <Input
              id="epicor-username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="epicor-password">Password</Label>
            <Input
              id="epicor-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="epicor-api-key">API Key (optional)</Label>
            <Input
              id="epicor-api-key"
              type="password"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="epicor-egress-proxy">
                Require static egress IP
              </Label>
              <p className="text-sm text-muted-foreground">
                Route all requests for this connection through Forgepoint's
                allowlistable IP.
              </p>
            </div>
            <Switch
              id="epicor-egress-proxy"
              checked={requiresEgressProxy}
              onCheckedChange={setRequiresEgressProxy}
            />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Validating..." : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
